/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useRingGroupData.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for fetching and managing single Ring Group data.
    Handles loading states and data adaptation to form state.

────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchRingGroupById } from '../api/ringGroupsApi';
import { MappedFormState, RingGroupResponse } from '../types';
import { mapResponseToState } from '../utils/mappers';
import { getDefaultFormState } from '../utils/formDefaults';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * Hook for fetching Ring Group data
 */
export function useRingGroupData(
  id: string | undefined,
  duplicateId: string | null
) {
  const location = useLocation();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [mappedData, setMappedData] = useState<MappedFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const activeRequestRef = useRef<{ loadId: string; cancelled: boolean } | null>(null);

  // Load ring group when id or duplicateId changes (for edit/duplicate mode)
  useEffect(() => {
    const loadId = duplicateId || id;
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Cancel any previous request
      if (activeRequestRef.current) {
        activeRequestRef.current.cancelled = true;
      }
      activeRequestRef.current = null;
      return;
    }

    // Only cancel if we're loading a DIFFERENT ID
    if (activeRequestRef.current && activeRequestRef.current.loadId !== loadId) {
      activeRequestRef.current.cancelled = true;
    }

    // Skip if we're already loading the same ID and it's not cancelled
    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey && 
        activeRequestRef.current && activeRequestRef.current.loadId === loadId && 
        !activeRequestRef.current.cancelled) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    setIsLoading(true);
    setMappedData(null); // Clear previous data while loading

    // Create a new active request
    const requestId = loadId;
    activeRequestRef.current = { loadId: requestId, cancelled: false };
    
    const fetchPromise = fetchRingGroupById(loadId);
    
    fetchPromise
      .then((group) => {
        // Capture the current state at the time the promise resolves
        const currentRequest = activeRequestRef.current;
        const currentLoadId = duplicateId || id;
        
        // Check if this request is still the active one and not cancelled
        const isRequestActive = currentRequest && 
                                currentRequest.loadId === requestId && 
                                !currentRequest.cancelled;
        
        // Check if the ID we're loading is still the one we want
        const isIdStillRelevant = requestId === currentLoadId;

        // Check if this request is still relevant
        if (!isRequestActive || !isIdStillRelevant) {
          return;
        }

        if (!group) {
          throw new Error('Ring group data is empty');
        }

        const mapped = mapResponseToState(group);

        // If duplicating, modify the name
        if (duplicateId) {
          mapped.formState.name = `${mapped.formState.name} (Copy)`;
        }

        // Double-check before updating state
        const finalCheck = activeRequestRef.current && 
                          activeRequestRef.current.loadId === requestId && 
                          !activeRequestRef.current.cancelled &&
                          requestId === (duplicateId || id);
        
        if (finalCheck) {
          setMappedData(mapped);
        }
      })
      .catch((error) => {
        const currentRequest = activeRequestRef.current;
        const currentLoadId = duplicateId || id;
        const isRequestActive = currentRequest && 
                                currentRequest.loadId === requestId && 
                                !currentRequest.cancelled;
        const isIdStillRelevant = requestId === currentLoadId;

        if (!isRequestActive || !isIdStillRelevant) {
          return;
        }
        
        const message = error instanceof Error ? error.message : t('ringGroups.messages.loadError');
        showError(message);
        lastLoadedIdRef.current = null;
        setMappedData(null);
      })
      .finally(() => {
        const currentRequest = activeRequestRef.current;
        const currentLoadId = duplicateId || id;
        const isRequestActive = currentRequest && 
                                currentRequest.loadId === requestId && 
                                !currentRequest.cancelled;
        const isIdStillRelevant = requestId === currentLoadId;
        const shouldSetLoading = isRequestActive && isIdStillRelevant;
        
        if (shouldSetLoading) {
          setIsLoading(false);
        }
      });

    return () => {
      // Only cancel if the ID actually changed (not just a re-render with same deps)
      const currentLoadId = duplicateId || id;
      if (activeRequestRef.current && 
          activeRequestRef.current.loadId === requestId && 
          currentLoadId !== requestId) {
        activeRequestRef.current.cancelled = true;
      }
    };
  }, [id, duplicateId, t, location.key, location.pathname, location.search]);

  // Load defaults for new ring groups (similar to Extensions)
  useEffect(() => {
    const currentId = id || 'new';
    const savedDataKey = 'ringGroups:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;
    
    // Always reset when navigating to new form (even if lastLoadedIdRef was 'new')
    // This ensures clean state after "Save and New"
    if (!id || id === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== currentId || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && currentId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }

        // Reset form to defaults first to ensure clean state
        // Create a new object reference each time to ensure React detects the change
        const defaultMappedData: MappedFormState = {
          formState: { ...getDefaultFormState() },
          members: [],
          extraSettings: {}
        };
        setMappedData(defaultMappedData);

        // Update refs to track this navigation
        lastLoadedIdRef.current = currentId;
        lastLocationKeyRef.current = locationKey;
      }
    } else if (id && id !== 'new') {
      // Track when we load an existing ring group
      lastLoadedIdRef.current = id;
      lastLocationKeyRef.current = location.key || location.pathname + location.search;
    }
  }, [id, location.key, location.pathname, location.search]);

  return {
    mappedData,
    isLoading
  };
}

